import React from 'react';
import { Search } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { TableFilterControl } from './types';

interface TableFilterBarProps {
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    controls?: TableFilterControl[];
    isDirty?: boolean;
    onClear: () => void;
    rightSlot?: React.ReactNode;
}

export function TableFilterBar({
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Search...',
    controls = [],
    isDirty = false,
    onClear,
    rightSlot
}: TableFilterBarProps) {
    return (
        <div className="w-full rounded-lg border bg-card p-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-1 flex-wrap items-center gap-2">
                    <div className="relative min-w-[240px] flex-1 lg:max-w-md">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={searchValue}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="pl-9"
                            aria-label="Search"
                        />
                    </div>
                    {controls.map((control) => (
                        <React.Fragment key={control.key}>
                            {control.type === 'daterange' ? (
                                <div className="flex items-center gap-1 rounded-md border bg-background px-2">
                                    <input
                                        type="date"
                                        className="h-10 bg-transparent text-sm focus:outline-none"
                                        value={control.value.split('|')[0] || ''}
                                        onChange={(e) => {
                                            const [, end] = control.value.split('|');
                                            control.onChange(`${e.target.value}|${end || ''}`);
                                        }}
                                        title={`${control.label} From`}
                                    />
                                    <span className="text-muted-foreground">-</span>
                                    <input
                                        type="date"
                                        className="h-10 bg-transparent text-sm focus:outline-none"
                                        value={control.value.split('|')[1] || ''}
                                        onChange={(e) => {
                                            const [start] = control.value.split('|');
                                            control.onChange(`${start || ''}|${e.target.value}`);
                                        }}
                                        title={`${control.label} To`}
                                    />
                                </div>
                            ) : control.type === 'asofdate' ? (
                                <div className="flex items-center gap-1 rounded-md border bg-background px-2">
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">As Of</span>
                                    <input
                                        type="date"
                                        className="h-10 bg-transparent text-sm focus:outline-none"
                                        value={control.value || ''}
                                        onChange={(e) => control.onChange(e.target.value)}
                                        title={`${control.label} As Of`}
                                    />
                                </div>
                            ) : control.type === 'select' ? (
                                <select
                                    value={control.value}
                                    onChange={(e) => control.onChange(e.target.value)}
                                    className="h-10 rounded-md border bg-background px-3 text-sm"
                                    aria-label={control.label}
                                    title={control.label}
                                >
                                    {control.options?.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className="flex flex-wrap items-center gap-1">
                                    {control.options?.map((option) => (
                                        <Button
                                            key={option.value}
                                            variant={control.value === option.value ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => control.onChange(option.value)}
                                            aria-label={`${control.label}: ${option.label}`}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                    {isDirty && (
                        <Button variant="ghost" size="sm" onClick={onClear}>
                            Clear Filters
                        </Button>
                    )}
                </div>
                {rightSlot && <div className="text-xs text-muted-foreground">{rightSlot}</div>}
            </div>
        </div>
    );
}
